import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAdjust,
  faArrowRight,
  faCheckCircle,
  faClock,
} from '@fortawesome/free-solid-svg-icons';
import { api } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import DashboardSummary from './DashboardSummary';
import RecentExpenses from './RecentExpenses';
import MonthlyChart from './MonthlyChart';
import CategoryBreakdown from './CategoryBreakdown';

const money = (value) => `£${(parseFloat(value) || 0).toFixed(2)}`;

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [recentExpenses, setRecentExpenses] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [salaryWarning, setSalaryWarning] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const currentYear = new Date().getFullYear();

        const [summaryRes, salariesRes, expensesRes, monthlyRes] = await Promise.all([
          api.get('/statistics/summary'),
          api.get('/users/salaries/current'),
          api.get('/expenses', { params: { limit: 5 } }),
          api.get('/statistics/monthly', { params: { year: currentYear } }),
        ]);

        setSummary(summaryRes.data.data);
        setSalaryWarning(
          !salariesRes.data.data.some((salary) => salary.userId === currentUser.id)
        );
        setRecentExpenses(expensesRes.data.data);
        setMonthlyData(monthlyRes.data.data);

        try {
          const categoryRes = await api.get('/statistics/categories', {
            params: {
              startDate: `${currentYear}-01-01`,
              endDate: `${currentYear}-12-31`,
            },
          });

          setCategoryData(
            (categoryRes.data.data || []).map((item) => ({
              id: item.Category?.id ?? null,
              name: item.Category?.name || 'Uncategorized',
              amount: parseFloat(item.totalAmount) || 0,
              color: item.Category?.color || '#9E9E9E',
              icon: item.Category?.icon || 'question',
            }))
          );
        } catch (categoryErr) {
          console.error('Error fetching category data:', categoryErr);
          setCategoryData([]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [currentUser.id]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="my-3">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  const settlement = summary?.settlement || {};

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>
          <p className="eyebrow">Overview</p>
          <h1>Home</h1>
          <p className="text-muted mb-0">Balances include partial contributions.</p>
        </div>
        <Button as={Link} to="/expenses/new" variant="primary">
          Add expense
        </Button>
      </div>

      {salaryWarning && (
        <Alert variant="warning" className="mb-4">
          <Alert.Heading>Salary needed</Alert.Heading>
          <p className="mb-3">
            Set this month&apos;s salary so salary-percentage splits stay accurate.
          </p>
          <div className="d-flex justify-content-end">
            <Button as={Link} to="/salary" variant="outline-warning">
              Set salary
            </Button>
          </div>
        </Alert>
      )}

      {summary && <DashboardSummary summary={summary} />}

      <section className="panel mb-4">
        <div className="panel-head d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h2 className="h5 mb-0">Settlement health</h2>
          <div className="d-flex gap-2">
            <Button as={Link} to="/statistics" variant="outline-primary" size="sm">
              Full stats
            </Button>
            <Button as={Link} to="/expenses" variant="outline-primary" size="sm">
              Review outstanding
              <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </Button>
          </div>
        </div>
        <div className="panel-body">
          <div className="settlement-grid">
            <div className="settlement-stat">
              <FontAwesomeIcon icon={faClock} className="text-warning me-2" />
              <div>
                <div className="fw-semibold">{settlement.pendingCount || 0} pending</div>
                <div className="small text-muted">No contribution yet</div>
              </div>
            </div>
            <div className="settlement-stat">
              <FontAwesomeIcon icon={faAdjust} className="text-info me-2" />
              <div>
                <div className="fw-semibold">{settlement.partialCount || 0} partial</div>
                <div className="small text-muted">
                  {money(settlement.amountContributed)} contributed so far
                </div>
              </div>
            </div>
            <div className="settlement-stat">
              <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
              <div>
                <div className="fw-semibold">{settlement.settledCount || 0} settled</div>
                <div className="small text-muted">Fully reimbursed (all time)</div>
              </div>
            </div>
            <div className="settlement-stat">
              <div>
                <div className="fw-semibold">{money(settlement.remainingReclaim)}</div>
                <div className="small text-muted">
                  Still open of {money(settlement.originalReclaimOutstanding)} reclaim
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="dashboard-grid mb-4">
        <section className="panel mb-3 mb-lg-0">
          <div className="panel-head">
            <h2 className="h5 mb-0">Monthly spend</h2>
          </div>
          <div className="panel-body">
            <MonthlyChart data={monthlyData} />
          </div>
        </section>
        <section className="panel">
          <div className="panel-head d-flex justify-content-between align-items-center">
            <h2 className="h5 mb-0">Categories</h2>
            <Button as={Link} to="/analysis" variant="outline-primary" size="sm">
              Analyse
            </Button>
          </div>
          <div className="panel-body">
            <CategoryBreakdown data={categoryData} year={new Date().getFullYear()} />
          </div>
        </section>
      </div>

      <section className="panel">
        <div className="panel-head d-flex justify-content-between align-items-center">
          <h2 className="h5 mb-0">Recent</h2>
          <Button as={Link} to="/expenses" variant="outline-primary" size="sm">
            View all
          </Button>
        </div>
        <div className="panel-body pt-0">
          <RecentExpenses expenses={recentExpenses} />
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
